<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import L from 'leaflet';
  import 'leaflet/dist/leaflet.css';
  import { currencyPHP } from '@epitome-analytics/dashboard-kit';

  import { store } from '../lib/stores.svelte.js';
  import {
    fetchProvinces, fetchCityRows,
    type ProvinceRow, type CityRow,
  } from '../lib/queries.js';
  import {
    loadProvinces, loadCities, sopColor,
    type ProvinceProps, type CityProps,
  } from '../lib/geo/index.js';
  import type { Feature, Geometry } from 'geojson';

  /* Hybrid choropleth: provinces at zoom ≤ ZOOM_SWITCH, cities above.
     City polygons (1.4 MB) lazy-load on first zoom-in. */

  const ZOOM_SWITCH = 7;

  let mapEl: HTMLDivElement;
  let map:   L.Map | undefined;
  let zoom = $state(6);

  let provData       = $state<Map<string, ProvinceRow>>(new Map());
  let cityData       = $state<Map<number, CityRow>>(new Map());
  let cityDataByPsgc = $state<Map<string, CityRow>>(new Map());

  let provLayer:  L.GeoJSON | undefined;
  let cityLayer:  L.GeoJSON | undefined;
  let citiesLoaded = false;

  let popup = $state<{
    kind: 'province' | 'city';
    name: string;
    cities?: number;
    my_gmv: number;
    sop: number;
    province?: string;   // province name for the drill action
    cityId?: number;
  } | null>(null);

  /* ── Data fetching: re-runs on period / filter changes ─────────────── */
  $effect(() => {
    void store.period; void store.principal; void store.productType; void store.brand; void store.sku_id;
    if (!store.period) return;

    Promise.all([
      fetchProvinces(store.period),
      fetchCityRows(store.period, 5000),
    ]).then(([prov, cities]) => {
      provData = new Map(prov.map((p) => [p.province, p]));
      cityData = new Map(cities.map((c) => [c.city_id, c]));
      cityDataByPsgc = new Map(cities.filter((c) => c.psgc).map((c) => [c.psgc, c]));
      restyleAll();
    }).catch(() => {});
  });

  /* ── Styles ─────────────────────────────────────────────────────────── */
  const PROV_BASE = { color: '#0E5A96', weight: 1.2, fillOpacity: 0.7 };
  const CITY_BASE = { color: '#FFFFFF', weight: 0.4, fillOpacity: 0.85 };

  const maxProvSop = $derived(Math.max(0, ...[...provData.values()].map((p) => p.sop)));
  const maxCitySop = $derived(Math.max(0, ...[...cityData.values()].map((c) => c.sop)));

  function styleProvince(f?: Feature<Geometry, ProvinceProps>): L.PathOptions {
    const name = f?.properties?.ADM2_EN ?? '';
    const row  = provData.get(name);
    return { ...PROV_BASE, fillColor: sopColor(row?.sop ?? 0, maxProvSop) };
  }

  function styleCity(f?: Feature<Geometry, CityProps>): L.PathOptions {
    const psgc = f?.properties?.adm3_psgc ?? '';
    const row  = cityDataByPsgc.get(String(psgc));
    return { ...CITY_BASE, fillColor: sopColor(row?.sop ?? 0, maxCitySop) };
  }

  function restyleAll() {
    provLayer?.setStyle(styleProvince);
    cityLayer?.setStyle(styleCity);
  }

  /* ── Layer construction ─────────────────────────────────────────────── */
  async function buildProvinceLayer() {
    const geo = await loadProvinces();
    provLayer = L.geoJSON(geo, {
      style: styleProvince,
      onEachFeature: (f, layer) => {
        const name = (f.properties as ProvinceProps).ADM2_EN;
        layer.on('click', () => {
          const row = provData.get(name);
          popup = {
            kind: 'province',
            name: row?.province ?? name,
            cities: row?.cities ?? 0,
            my_gmv: row?.my_gmv ?? 0,
            sop:    row?.sop    ?? 0,
            province: row?.province ?? name,
          };
        });
        layer.bindTooltip(name, { sticky: true, direction: 'top' });
      },
    });
    if (map && zoom <= ZOOM_SWITCH) provLayer.addTo(map);
  }

  async function buildCityLayer() {
    if (citiesLoaded) return;
    citiesLoaded = true;
    const geo = await loadCities();
    cityLayer = L.geoJSON(geo, {
      style: styleCity,
      onEachFeature: (f, layer) => {
        const props = f.properties as CityProps;
        const psgc  = String(props.adm3_psgc);
        layer.on('click', () => {
          const row = cityDataByPsgc.get(psgc);
          // City click is a HEATMAP-ONLY interaction — show metrics in
          // popup, no drill. Drill happens at province level.
          popup = row
            ? { kind: 'city', name: `${row.city}, ${row.province}`,
                my_gmv: row.my_gmv, sop: row.sop, province: row.province }
            : { kind: 'city', name: props.adm3_en, my_gmv: 0, sop: 0 };
        });
        layer.bindTooltip(props.adm3_en, { sticky: true, direction: 'top' });
      },
    });
    if (map && zoom > ZOOM_SWITCH) cityLayer.addTo(map);
  }

  function syncLayers() {
    if (!map) return;
    const useCities = zoom > ZOOM_SWITCH;
    if (useCities) {
      if (provLayer && map.hasLayer(provLayer)) map.removeLayer(provLayer);
      if (!cityLayer) { buildCityLayer(); return; }
      if (!map.hasLayer(cityLayer)) cityLayer.addTo(map);
    } else {
      if (cityLayer && map.hasLayer(cityLayer)) map.removeLayer(cityLayer);
      if (provLayer && !map.hasLayer(provLayer)) provLayer.addTo(map);
    }
  }

  onMount(() => {
    map = L.map(mapEl, {
      center: [12.5, 122],
      zoom: 6,
      minZoom: 5,
      maxZoom: 12,
      attributionControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);

    map.on('zoomend', () => {
      if (!map) return;
      zoom = map.getZoom();
      syncLayers();
    });

    buildProvinceLayer();
  });

  onDestroy(() => {
    map?.remove();
    map = undefined;
  });

  const grain = $derived(zoom > ZOOM_SWITCH ? 'City' : 'Province');
</script>

<div class="map-wrap">
  <div class="map" bind:this={mapEl}></div>

  <div class="grain-badge">
    <span class="dot"></span>
    {grain} choropleth · zoom {zoom}
  </div>

  {#if popup}
    <aside class="popup">
      <header>
        <h3>{popup.name}</h3>
        <button onclick={() => (popup = null)}>×</button>
      </header>
      <dl>
        {#if popup.cities !== undefined}
          <dt>Active cities</dt><dd>{popup.cities}</dd>
        {/if}
        <dt>My GMV</dt>       <dd>{currencyPHP.formatPHPShort(popup.my_gmv)}</dd>
        <dt>Size of Prize</dt><dd>{currencyPHP.formatPHPShort(popup.sop)}</dd>
      </dl>
      {#if popup.kind === 'province' && popup.province}
        <button class="drill" onclick={() => store.selectProvince(popup!.province!)}>
          Drill into {popup.province} →
        </button>
      {/if}
    </aside>
  {/if}
</div>

<style>
  .map-wrap {
    position: relative;
    width: 100%;
    height: 600px;
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  .map { width: 100%; height: 100%; }
  .grain-badge {
    position: absolute;
    top: var(--space-md);
    left: var(--space-md);
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    padding: 4px 12px;
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide);
    color: var(--color-text-muted);
    z-index: 1000;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: var(--shadow-card);
  }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--color-teal); }
  .popup {
    position: absolute;
    top: var(--space-lg);
    right: var(--space-lg);
    width: 280px;
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-modal);
    padding: var(--space-md) var(--space-lg);
    font-family: var(--font-ui);
    font-size: var(--text-sm);
    z-index: 1000;
  }
  .popup header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-sm); }
  .popup h3 { margin: 0; font-family: var(--font-display); font-size: var(--text-md); text-transform: uppercase; color: var(--color-navy); }
  .popup button { background: transparent; border: none; cursor: pointer; font-size: var(--text-lg); color: var(--color-text-muted); }
  dl { display: grid; grid-template-columns: 1fr auto; gap: 4px 12px; margin: 0; }
  dt { color: var(--color-text-muted); }
  dd { margin: 0; font-weight: var(--weight-semibold); text-align: right; }
  .drill {
    margin-top: var(--space-md);
    width: 100%;
    padding: 8px 12px;
    background: var(--color-blue);
    color: var(--color-white);
    border: none;
    border-radius: var(--radius-sm);
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide);
    cursor: pointer;
  }
  .drill:hover { background: var(--color-navy); }
</style>
